const API_BASE = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'auth_token';
const REMEMBER_KEY = 'auth_remember';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY) || null;
    this.remember = localStorage.getItem(REMEMBER_KEY) === '1';
  }

  setToken(token, remember = this.remember) {
    this.token = token;
    this.remember = !!remember;

    localStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_KEY);

    if (token) {
      if (this.remember) {
        localStorage.setItem(TOKEN_KEY, token);
        localStorage.setItem(REMEMBER_KEY, '1');
      } else {
        sessionStorage.setItem(TOKEN_KEY, token);
        localStorage.removeItem(REMEMBER_KEY);
      }
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }
  }

  getAuthHeaders(extraHeaders = {}) {
    const headers = { ...extraHeaders };
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    const isFormData = options.body instanceof FormData;
    const headers = this.getAuthHeaders(isFormData ? options.headers : { 'Content-Type': 'application/json', ...(options.headers || {}) });

    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await res.json() : {};

    if (!res.ok) {
      throw new Error(data.error || 'Request failed');
    }
    return data;
  }

  login(username, password, remember = false) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password, remember }),
    });
  }

  logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  getMe() {
    return this.request('/auth/me');
  }

  changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  // Users
  getUsers() {
    return this.request('/users');
  }

  createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  resetUserPassword(id, newPassword) {
    return this.request(`/users/${id}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ new_password: newPassword }),
    });
  }

  deleteUser(id) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  // Tasks
  getTasks() {
    return this.request('/tasks');
  }

  getTask(id) {
    return this.request(`/tasks/${id}`);
  }

  createTask(data) {
    return this.request('/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  updateTask(id, data) {
    return this.request(`/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  moveTask(id, status, sortOrder) {
    return this.request(`/tasks/${id}/move`, {
      method: 'PATCH',
      body: JSON.stringify({ status, sort_order: sortOrder }),
    });
  }

  reorderTasks(orders) {
    return this.request('/tasks/reorder', {
      method: 'PATCH',
      body: JSON.stringify({ orders }),
    });
  }
  deleteTask(id) {
    return this.request(`/tasks/${id}`, { method: 'DELETE' });
  }

  saveTaskProgressNote(taskId, note) {
    return this.request(`/tasks/${taskId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'note', note }),
    });
  }

  createTaskProgressTodo(taskId, text) {
    return this.request(`/tasks/${taskId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'create_todo', text }),
    });
  }

  updateTaskProgressTodo(taskId, todoId, data) {
    return this.request(`/tasks/${taskId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'update_todo', todo_id: todoId, ...data }),
    });
  }

  deleteTaskProgressTodo(taskId, todoId) {
    return this.request(`/tasks/${taskId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'delete_todo', todo_id: todoId }),
    });
  }


  // Comments
  getComments(taskId, page = 1) {
    return this.request(`/comments/${taskId}?page=${page}`);
  }

  addComment(taskId, content) {
    return this.request(`/comments/${taskId}`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  deleteComment(id) {
    return this.request(`/comments/${id}/delete`, { method: 'DELETE' });
  }

  // Resources & Deliverables
  uploadFile(endpoint, file) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers: {},
    });
  }

  addResourceFile(taskId, file) {
    return this.uploadFile(`/upload/${taskId}/resource`, file);
  }

  addDeliverableFile(taskId, file) {
    return this.uploadFile(`/upload/${taskId}/deliverable`, file);
  }

  addResourceText(taskId, text) {
    return this.request(`/upload/${taskId}/resource-text`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  addDeliverableText(taskId, text) {
    return this.request(`/upload/${taskId}/deliverable-text`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  removeResource(id) {
    return this.request(`/upload/${id}/resource`, { method: 'DELETE' });
  }

  removeDeliverable(id) {
    return this.request(`/upload/${id}/deliverable`, { method: 'DELETE' });
  }

  // Work Requests
  getRequests(status) {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/requests${query}`);
  }

  submitRequest(data) {
    return this.request('/requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  handleRequest(id, action) {
    return this.request(`/requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ action }),
    });
  }

  // Profile
  getProfile() {
    return this.request('/profile');
  }

  updateProfile(data) {
    return this.request('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Logs
  getLogs({ page = 1, limit = 30, query = '' } = {}) {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (query) params.set('query', query);
    return this.request(`/logs?${params.toString()}`);
  }

  // Settings
  getSettings() {
    return this.request('/settings');
  }

  updateSettings(data) {
    return this.request('/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }
}

const api = new ApiClient();
export default api;
